import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';

/** NIP-84 highlight event kind. */
export const HIGHLIGHT_KIND = 9802;

/** Permalink path for a highlight (nevent with author hint). */
export function highlightPath(highlight: Highlight): string {
  return `/${nip19.neventEncode({ id: highlight.event.id, author: highlight.event.pubkey })}`;
}

/** A reference from a highlight to the content it was taken from. */
export type HighlightSource =
  | { type: 'article'; coordinate: string; kind: number; pubkey: string; identifier: string }
  | { type: 'note'; id: string }
  | { type: 'url'; url: string };

/** A parsed, validated NIP-84 highlight. */
export interface Highlight {
  event: NostrEvent;
  /** The highlighted text (event content). */
  quote: string;
  /** Optional commentary making this a "quote highlight" (`comment` tag). */
  comment?: string;
  /** Optional surrounding text giving context to the quote (`context` tag). */
  context?: string;
  /** Resolved source references, in tag order. */
  sources: HighlightSource[];
  /** Pubkeys of the original authors (`p` tags without a `mention` marker). */
  sourceAuthors: string[];
}

/** Sanity limits to keep abusive/malformed events out of the UI. */
const MAX_QUOTE_LENGTH = 10_000;
const MAX_COMMENT_LENGTH = 5_000;
const MAX_CONTEXT_LENGTH = 20_000;
const MAX_SOURCES = 8;

const HEX_64 = /^[0-9a-f]{64}$/i;

/**
 * Parse a `kind:pubkey:identifier` address coordinate. The identifier itself
 * may contain colons, so only the first two separators are structural.
 */
export function parseAddressCoordinate(coordinate: string): { kind: number; pubkey: string; identifier: string } | null {
  const firstColon = coordinate.indexOf(':');
  const secondColon = coordinate.indexOf(':', firstColon + 1);
  if (firstColon < 1 || secondColon < 0) return null;

  const kind = Number(coordinate.slice(0, firstColon));
  const pubkey = coordinate.slice(firstColon + 1, secondColon);
  const identifier = coordinate.slice(secondColon + 1);

  if (!Number.isInteger(kind) || kind < 0 || kind > 65535) return null;
  if (!HEX_64.test(pubkey)) return null;

  return { kind, pubkey, identifier };
}

/** Validate and parse a kind 9802 event. Returns `null` for unusable events. */
export function parseHighlight(event: NostrEvent): Highlight | null {
  if (event.kind !== HIGHLIGHT_KIND) return null;

  const quote = event.content.trim();
  const comment = event.tags.find(([name]) => name === 'comment')?.[1]?.trim() || undefined;
  const context = event.tags.find(([name]) => name === 'context')?.[1]?.trim() || undefined;

  // A highlight must carry either quoted text or a comment (non-text media
  // highlights exist but have nothing to display in a reading client).
  if (!quote && !comment) return null;
  if (quote.length > MAX_QUOTE_LENGTH) return null;
  if (comment && comment.length > MAX_COMMENT_LENGTH) return null;
  if (context && context.length > MAX_CONTEXT_LENGTH) return null;

  const sources: HighlightSource[] = [];
  const sourceAuthors: string[] = [];

  for (const tag of event.tags) {
    const [name, value, , marker] = tag;
    if (!value) continue;

    switch (name) {
      case 'a': {
        if (sources.length >= MAX_SOURCES) break;
        const parsed = parseAddressCoordinate(value);
        if (parsed) {
          sources.push({ type: 'article', coordinate: value, ...parsed });
        }
        break;
      }
      case 'e': {
        if (sources.length >= MAX_SOURCES) break;
        if (HEX_64.test(value)) {
          sources.push({ type: 'note', id: value });
        }
        break;
      }
      case 'r': {
        if (sources.length >= MAX_SOURCES) break;
        // URLs mentioned inside the comment are marked `mention`; only
        // source URLs (explicit `source` marker or unmarked) are sources.
        if (marker === 'mention') break;
        try {
          const url = new URL(value);
          if (url.protocol === 'http:' || url.protocol === 'https:') {
            sources.push({ type: 'url', url: url.href });
          }
        } catch {
          // Not a valid URL — ignore.
        }
        break;
      }
      case 'p': {
        // Mentioned pubkeys (from the comment) are not attribution.
        if (marker === 'mention') break;
        if (HEX_64.test(value) && !sourceAuthors.includes(value)) {
          sourceAuthors.push(value);
        }
        break;
      }
    }
  }

  return { event, quote, comment, context, sources, sourceAuthors };
}

/** Get the lookup key a source resolves under in a source-event map. */
export function sourceKey(source: HighlightSource): string {
  switch (source.type) {
    case 'article':
      return source.coordinate;
    case 'note':
      return source.id;
    case 'url':
      return source.url;
  }
}

/** Pick the richest source to display: article > note > URL. */
export function primarySource(sources: HighlightSource[]): HighlightSource | null {
  return (
    sources.find((s) => s.type === 'article') ??
    sources.find((s) => s.type === 'note') ??
    sources.find((s) => s.type === 'url') ??
    null
  );
}
